import React, { useState, useCallback } from 'react';
import { api } from '../App';
import toast from 'react-hot-toast';

const PERMIT_COLORS = {
  solar_installation: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' },
  solar_panels: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' },
  roofing: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  electrical_panel_upgrade: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  electrical: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  ev_charger: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  home_battery: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  energy_efficiency: { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200' },
  smart_home: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200' },
  hvac: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  window_replacement: { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200' },
  insulation: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
};

const QUICK_LOCATIONS = [
  { label: 'NYC — All Boroughs', value: 'New York' },
  { label: 'Manhattan', value: 'Manhattan' },
  { label: 'Brooklyn', value: 'Brooklyn' },
  { label: 'Queens', value: 'Queens' },
  { label: 'Bronx', value: 'Bronx' },
  { label: 'Staten Island', value: 'Staten Island' },
  { label: 'New Jersey', value: 'New Jersey' },
  { label: 'Bergen County NJ', value: 'Bergen County' },
  { label: 'Buffalo', value: 'Buffalo' },
  { label: 'Los Angeles', value: 'Los Angeles' },
  { label: 'Chicago', value: 'Chicago' },
  { label: 'Austin', value: 'Austin' },
  { label: 'Seattle', value: 'Seattle' },
  { label: 'Dallas', value: 'Dallas' },
  { label: 'San Francisco', value: 'San Francisco' },
  { label: 'Philadelphia', value: 'Philadelphia' },
  { label: 'Mesa AZ', value: 'Mesa' },
  { label: 'Cambridge MA', value: 'Cambridge' },
];

function ScoreRing({ score }) {
  const r = 28, c = 2 * Math.PI * r;
  const color = score >= 85 ? '#16A34A' : score >= 70 ? '#F59E0B' : '#DC2626';
  return (
    <div className="relative w-[72px] h-[72px] flex-shrink-0">
      <svg viewBox="0 0 64 64" className="w-full h-full -rotate-90">
        <circle cx="32" cy="32" r={r} fill="none" stroke="#E5E7EB" strokeWidth="5" />
        <circle cx="32" cy="32" r={r} fill="none" stroke={color} strokeWidth="5" strokeDasharray={c} strokeDashoffset={c - (score / 100) * c} strokeLinecap="round" className="transition-all duration-700" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-lg font-bold font-mono tabular-nums" style={{ color }}>{score}%</span>
      </div>
    </div>
  );
}

export default function PermitIntelligence() {
  const [location, setLocation] = useState('');
  const [count, setCount] = useState(10);
  const [leads, setLeads] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [scanDone, setScanDone] = useState(false);
  const [scanEngine, setScanEngine] = useState(null);
  const [verifying, setVerifying] = useState({}); // { leadIdx: true/false }
  const [enriching, setEnriching] = useState({}); // { leadIdx: true/false }
  const [bulkVerifying, setBulkVerifying] = useState(false);
  const [pricingInfo, setPricingInfo] = useState(null);
  const [permitHist, setPermitHist] = useState({}); // { idx: data }
  const [permitLoading, setPermitLoading] = useState({});

  const pullPermitHistory = async (idx) => {
    const lead = leads[idx];
    setPermitLoading(prev => ({ ...prev, [idx]: true }));
    try {
      const res = await api.post('/api/enrich/shovels-permits', {
        address: lead.address, city: lead.city, state: lead.state, zip: lead.zip, lead_id: lead._id || null,
      });
      setPermitHist(prev => ({ ...prev, [idx]: res.data }));
      if (!res.data.found) toast('No permit records found', { icon: 'ℹ️' });
      else if (res.data.has_existing_solar) toast.error('⚠️ Already has solar (competitor)');
      else toast.success(`${res.data.permit_count} permits · ${res.data.tags.length} signals`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Permit lookup failed');
    } finally { setPermitLoading(prev => ({ ...prev, [idx]: false })); }
  };

  // Fetch pricing info on mount
  React.useEffect(() => {
    api.get('/api/enrich/pricing').then(r => setPricingInfo(r.data)).catch(() => {});
  }, []);

  const runScan = useCallback(async (loc) => {
    const target = loc || location;
    if (!target.trim()) { toast.error('Enter a zip code or city name'); return; }
    setScanning(true);
    setScanDone(false);
    setLeads([]);
    setSelected(new Set());
    try {
      const res = await api.post('/api/scanner/scan', { location: target, count });
      setLeads(res.data.leads || []);
      setScanEngine(res.data.engine || null);
      setScanDone(true);
      if ((res.data.leads || []).length === 0) {
        toast(`No live results for ${target}. Try a covered city (Austin, Chicago, Seattle...).`, { icon: 'ℹ️' });
      } else {
        toast.success(`${res.data.count} leads found in ${target}`);
      }
    } catch (err) {
      toast.error('Scan failed');
    } finally { setScanning(false); }
  }, [location, count]);

  // ── Free Smart Enrichment ──
  const freeEnrich = async (idx) => {
    const lead = leads[idx];
    setEnriching(prev => ({ ...prev, [idx]: true }));
    try {
      const res = await api.post('/api/enrich/free-phone-search', {
        owner_name: lead.owner_name || lead.name,
        zip_code: lead.zip,
        address: lead.address,
        state: lead.state,
        lead_id: lead._id || null,
      });
      const data = res.data;
      // Update lead in state
      setLeads(prev => {
        const updated = [...prev];
        const phone = data.phone?.best_phone;
        if (phone && !phone.includes('---')) {
          updated[idx] = { ...updated[idx], phone, verification: { ...updated[idx].verification, phone_confidence: data.phone.best_confidence, phone_source: 'smart_search', all_phones: data.phone.phones_found?.map(p => p.phone) || [], name_verified: data.name_verification?.verified, name_confidence: data.name_verification?.confidence } };
        } else {
          updated[idx] = { ...updated[idx], verification: { ...updated[idx].verification, name_verified: data.name_verification?.verified, name_confidence: data.name_verification?.confidence } };
        }
        return updated;
      });
      const phoneCount = data.phone?.phones_found?.filter(p => !p.phone.includes('---')).length || 0;
      toast.success(`Free enrichment: ${phoneCount} phone(s) found, name ${data.name_verification?.verified ? 'confirmed' : 'unconfirmed'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Enrichment failed');
    } finally { setEnriching(prev => ({ ...prev, [idx]: false })); }
  };

  // ── Premium Twilio Verify ──
  const premiumVerify = async (idx) => {
    const lead = leads[idx];
    setVerifying(prev => ({ ...prev, [idx]: true }));
    try {
      const res = await api.post('/api/enrich/verify-phone', {
        phone_number: lead.phone && !lead.phone.includes('---') ? lead.phone : null,
        lead_id: lead._id || null,
        owner_name: lead.owner_name || lead.name,
        zip_code: lead.zip,
        address: lead.address,
        state: lead.state,
      });
      const v = res.data.verification;
      setLeads(prev => {
        const updated = [...prev];
        updated[idx] = {
          ...updated[idx],
          phone: v.national_format || v.phone_number || updated[idx].phone,
          verification: {
            ...updated[idx].verification,
            phone_verified: v.valid,
            phone_confidence: 95,
            phone_source: 'twilio_lookup_v2',
            carrier_name: v.carrier_name,
            line_type: v.line_type,
            caller_name: v.caller_name,
          }
        };
        return updated;
      });
      if (pricingInfo) setPricingInfo(p => ({ ...p, user_credits: res.data.remaining_credits }));
      toast.success(`Verified! ${v.carrier_name || ''} ${v.line_type || ''} — $${res.data.cost} charged`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed');
    } finally { setVerifying(prev => ({ ...prev, [idx]: false })); }
  };

  // ── Bulk Premium Verify ──
  const bulkVerify = async () => {
    const ids = leads.filter((l, i) => selected.has(i) && l._id).map(l => l._id);
    if (ids.length === 0) { toast.error('Select leads with saved IDs to bulk verify'); return; }
    setBulkVerifying(true);
    try {
      const res = await api.post('/api/enrich/verify-phone-bulk', { lead_ids: ids });
      if (pricingInfo) setPricingInfo(p => ({ ...p, user_credits: res.data.remaining_credits }));
      toast.success(`Bulk verified: ${res.data.verified_count}/${res.data.total_attempted} — $${res.data.total_cost} charged`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk verification failed');
    } finally { setBulkVerifying(false); }
  };

  const toggleSelect = (idx) => {
    setSelected(prev => {
      const n = new Set(prev);
      n.has(idx) ? n.delete(idx) : n.add(idx);
      return n;
    });
  };

  const selectAll = () => setSelected(new Set(leads.map((_, i) => i)));
  const clearSelection = () => setSelected(new Set());

  const saveSelected = async () => {
    const toSave = leads.filter((_, i) => selected.has(i));
    if (toSave.length === 0) { toast.error('Select leads to save'); return; }
    setSaving(true);
    try {
      const res = await api.post('/api/scanner/scan-and-save', { location: location || 'Saved', count: toSave.length });
      toast.success(`${toSave.length} leads saved to database!`);
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const saveAll = async () => {
    if (leads.length === 0) return;
    setSaving(true);
    try {
      const res = await api.post('/api/scanner/scan-and-save', { location, count: leads.length });
      toast.success(`${leads.length} leads saved to database!`);
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="permit-intelligence-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-text-primary tracking-tight">Permit Intelligence</h1>
          <p className="text-text-muted text-sm mt-1">Scan public permit records to find high-value solar leads instantly</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {pricingInfo && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-surface border border-border text-xs" data-testid="credits-display">
              <i className="fa-solid fa-coins text-amber-500" />
              <span className="text-text-muted">Credits:</span>
              <span className="font-bold text-text-primary">${(pricingInfo.user_credits || 0).toFixed(2)}</span>
            </div>
          )}
          {selected.size > 0 && (
            <button onClick={bulkVerify} disabled={bulkVerifying} className="btn-secondary text-xs py-2 border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="bulk-verify-btn">
              <i className="fa-solid fa-phone-volume mr-1.5" />
              {bulkVerifying ? 'Verifying...' : `Premium Verify ${selected.size} ($${(selected.size * 1.35).toFixed(2)})`}
            </button>
          )}
          {leads.length > 0 && (
            <button onClick={saveAll} disabled={saving} className="btn-primary text-xs py-2" data-testid="save-all-btn">
              <i className="fa-solid fa-database mr-1.5" />{saving ? 'Saving...' : `Save All ${leads.length} Leads`}
            </button>
          )}
        </div>
      </div>

      {/* Search Bar */}
      <div className="card-interactive p-6" data-testid="scanner-controls">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-2">Location (Zip Code or City)</label>
            <div className="relative">
              <i className="fa-solid fa-location-dot absolute left-4 top-1/2 -translate-y-1/2 text-text-muted text-sm" />
              <input
                type="text" value={location} onChange={e => setLocation(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && runScan()}
                placeholder="e.g., 95822 or Sacramento" className="input-field pl-10" data-testid="scanner-location-input"
              />
            </div>
          </div>
          <div className="w-32">
            <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-2">Lead Count</label>
            <input type="number" min={1} max={25} value={count} onChange={e => setCount(parseInt(e.target.value) || 10)} className="input-field" data-testid="scanner-count-input" />
          </div>
          <div className="flex items-end">
            <button onClick={() => runScan()} disabled={scanning} className="btn-primary py-2.5 px-8 whitespace-nowrap" data-testid="run-scan-btn">
              {scanning ? <><i className="fa-solid fa-spinner fa-spin mr-2" />Scanning...</> : <><i className="fa-solid fa-tower-broadcast mr-2" />Find Leads</>}
            </button>
          </div>
        </div>

        {/* Quick Locations */}
        <div className="flex flex-wrap gap-2 mt-4">
          <span className="text-[10px] font-bold uppercase tracking-widest text-text-muted self-center mr-1">Quick:</span>
          {QUICK_LOCATIONS.map(loc => (
            <button key={loc.value} onClick={() => { setLocation(loc.value); runScan(loc.value); }}
              className="px-3 py-1.5 rounded-lg text-xs font-medium border border-border text-text-secondary hover:border-primary hover:text-primary hover:bg-primary/5 transition-all"
              data-testid={`quick-loc-${loc.value.toLowerCase().replace(/\s/g, '-')}`}
            >{loc.label}</button>
          ))}
        </div>
      </div>

      {/* Scanning Animation */}
      {scanning && (
        <div className="card-interactive p-8 text-center" data-testid="scanning-animation">
          <div className="relative w-20 h-20 mx-auto mb-4">
            <div className="absolute inset-0 rounded-full border-4 border-primary/20 animate-ping" />
            <div className="absolute inset-2 rounded-full border-4 border-primary/30 animate-ping" style={{ animationDelay: '0.3s' }} />
            <div className="absolute inset-4 rounded-full bg-primary/10 flex items-center justify-center">
              <i className="fa-solid fa-satellite-dish text-primary text-2xl animate-pulse" />
            </div>
          </div>
          <p className="text-sm font-semibold text-text-primary">Scanning permit records...</p>
          <p className="text-xs text-text-muted mt-1">Querying public databases for {location || 'your location'}</p>
          <div className="mt-4 max-w-xs mx-auto">
            <div className="h-2 bg-border rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-primary to-emerald-400 rounded-full animate-scan" style={{ width: '60%' }} />
            </div>
          </div>
        </div>
      )}

      {/* Results Header */}
      {scanDone && leads.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <p className="text-sm font-semibold text-text-primary"><span className="text-primary font-bold text-lg">{leads.length}</span> leads found</p>
            {scanEngine === 'permit_engine' && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 border border-primary/20 text-[10px] font-bold text-primary" data-testid="engine-badge">
                <i className="fa-solid fa-satellite-dish" />LIVE PERMIT DATA
              </span>
            )}
            {scanEngine === 'attom_property' && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 border border-blue-200 text-[10px] font-bold text-blue-700" data-testid="engine-badge">
                <i className="fa-solid fa-house" />PROPERTY DATA
              </span>
            )}
            <div className="flex gap-2">
              <button onClick={selectAll} className="text-xs text-primary font-medium hover:underline" data-testid="select-all-btn">Select All</button>
              <button onClick={clearSelection} className="text-xs text-text-muted font-medium hover:underline" data-testid="clear-selection-btn">Clear</button>
            </div>
            {selected.size > 0 && (
              <button onClick={saveSelected} disabled={saving} className="btn-secondary text-xs py-1.5 px-3" data-testid="save-selected-btn">
                <i className="fa-solid fa-plus mr-1" />{saving ? 'Saving...' : `Save ${selected.size} Selected`}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-text-muted">
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-primary rounded-full" />High (85+)</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-yellow-500 rounded-full" />Medium (70-84)</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-red-500 rounded-full" />Low (&lt;70)</span>
          </div>
        </div>
      )}

      {/* Lead Cards */}
      <div className="space-y-3">
        {leads.map((lead, i) => {
          const pc = PERMIT_COLORS[lead.permit_type] || { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200' };
          const isSelected = selected.has(i);
          return (
            <div key={i} className={`card-interactive p-5 animate-slide-up cursor-pointer transition-all ${isSelected ? 'ring-2 ring-primary border-primary/30' : ''}`}
              style={{ animationDelay: `${i * 50}ms` }}
              onClick={() => toggleSelect(i)}
              data-testid={`permit-lead-${i}`}
            >
              <div className="flex items-start gap-5">
                {/* Checkbox */}
                <div className={`mt-1 w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${isSelected ? 'bg-primary border-primary' : 'border-border'}`}>
                  {isSelected && <i className="fa-solid fa-check text-white text-[10px]" />}
                </div>

                {/* Score Ring */}
                <ScoreRing score={lead.score} />

                {/* Lead Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-semibold text-text-primary">{lead.name || 'Unknown Homeowner'}</h3>
                        {lead.verification?.name_verified && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 border border-green-200 text-[9px] font-bold text-green-700" data-testid={`name-verified-${i}`}>
                            <i className="fa-solid fa-circle-check text-green-500" />CONFIRMED
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-text-muted mt-0.5"><i className="fa-solid fa-location-dot mr-1" />{lead.address}{lead.city ? `, ${lead.city}` : ''} {lead.state}</p>
                    </div>
                    <span className={`badge ${pc.bg} ${pc.text} border ${pc.border} whitespace-nowrap`}>
                      {(lead.permit_type || '').replace(/_/g, ' ')}
                    </span>
                    {lead.data_source && (
                      <span className="badge bg-green-50 text-green-700 border border-green-200 text-[9px] whitespace-nowrap">
                        <i className="fa-solid fa-circle-check mr-1 text-green-500" />
                        {lead.source === 'permit_scanner' ? 'LIVE PERMIT' : lead.data_source.includes('ATTOM') || lead.data_source.includes('Google') ? 'REAL DATA' : 'EST.'}
                      </span>
                    )}
                  </div>

                  {/* Live Permit Signal Banner */}
                  {lead.permit_signal && (
                    <div className="flex flex-wrap items-center gap-2 mb-3 p-2.5 rounded-lg bg-primary/5 border border-primary/15" data-testid={`permit-signal-${i}`}>
                      <i className="fa-solid fa-bolt text-primary text-xs" />
                      <span className="text-xs font-semibold text-text-primary">{lead.permit_signal}</span>
                      {lead.permit_days_ago != null && (
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${lead.permit_days_ago <= 30 ? 'bg-red-100 text-red-700' : lead.permit_days_ago <= 90 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
                          {lead.permit_days_ago === 0 ? 'Today' : `${lead.permit_days_ago}d ago`}
                        </span>
                      )}
                      {lead.permit_number && <span className="text-[10px] text-text-muted font-mono">#{lead.permit_number}</span>}
                      {lead.contractor_name && <span className="text-[10px] text-text-muted"><i className="fa-solid fa-helmet-safety mr-1" />{lead.contractor_name}</span>}
                    </div>
                  )}

                  {/* Contact Info with Verification Badges */}
                  <div className="flex flex-wrap gap-4 mb-3 text-xs">
                    {lead.email && (
                      <span className="flex items-center gap-1.5 text-text-secondary">
                        <i className="fa-solid fa-envelope text-accent text-[10px]" />{lead.email}
                        {lead.verification?.email_verified && (
                          <span className="ml-1 w-3.5 h-3.5 rounded-full bg-green-500 inline-flex items-center justify-center" title="Email verified via SMTP"><i className="fa-solid fa-check text-white text-[7px]" /></span>
                        )}
                      </span>
                    )}
                    {lead.phone && (
                      <span className="flex items-center gap-1.5 text-text-secondary">
                        <i className="fa-solid fa-phone text-primary text-[10px]" />{lead.phone}
                        {lead.verification?.phone_verified ? (
                          <span className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-green-50 border border-green-200 text-[8px] font-bold text-green-700" data-testid={`phone-verified-${i}`}>
                            <i className="fa-solid fa-circle-check text-green-500" />VERIFIED
                          </span>
                        ) : lead.phone.includes('---') ? (
                          <span className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-[8px] font-bold text-amber-700">
                            <i className="fa-solid fa-triangle-exclamation text-amber-500" />AREA CODE ONLY
                          </span>
                        ) : null}
                      </span>
                    )}
                    <span className="flex items-center gap-1.5 text-text-secondary">
                      <i className="fa-solid fa-bolt text-secondary text-[10px]" />{lead.utility}
                    </span>
                  </div>

                  {/* Verification Summary Bar */}
                  {lead.verification && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {lead.verification.name_verified && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-green-50 border border-green-100 text-[9px] text-green-700 font-medium">
                          <i className="fa-solid fa-user-check text-green-500" />Name: {lead.verification.name_confidence}% confidence
                        </span>
                      )}
                      {lead.verification.phone_verified && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-green-50 border border-green-100 text-[9px] text-green-700 font-medium">
                          <i className="fa-solid fa-phone-volume text-green-500" />Phone: {lead.verification.phone_confidence}% confidence
                        </span>
                      )}
                      {lead.verification.carrier_name && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-indigo-50 border border-indigo-100 text-[9px] text-indigo-700 font-medium">
                          <i className="fa-solid fa-tower-cell text-indigo-500" />{lead.verification.carrier_name} ({lead.verification.line_type})
                        </span>
                      )}
                      {lead.verification.caller_name && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-purple-50 border border-purple-100 text-[9px] text-purple-700 font-medium">
                          <i className="fa-solid fa-id-card text-purple-500" />CNAM: {lead.verification.caller_name}
                        </span>
                      )}
                      {lead.verification.email_confidence > 20 && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-blue-50 border border-blue-100 text-[9px] text-blue-700 font-medium">
                          <i className="fa-solid fa-envelope-circle-check text-blue-500" />Email: {Math.round(lead.verification.email_confidence)}% confidence
                        </span>
                      )}
                    </div>
                  )}

                  {/* ── Enrich / Verify Actions ── */}
                  <div className="flex flex-wrap gap-2 mb-3" onClick={(e) => e.stopPropagation()}>
                    {/* Free Smart Enrich */}
                    <button
                      onClick={() => freeEnrich(i)}
                      disabled={enriching[i]}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-alt border border-border text-[10px] font-semibold text-text-secondary hover:border-primary/30 hover:text-primary transition-all disabled:opacity-50"
                      data-testid={`free-enrich-btn-${i}`}
                    >
                      <i className={`fa-solid ${enriching[i] ? 'fa-spinner fa-spin' : 'fa-magnifying-glass'} text-primary`} />
                      {enriching[i] ? 'Searching...' : 'Free Enrich'}
                      <span className="text-[8px] text-green-600 ml-1 font-bold">FREE</span>
                    </button>

                    {/* Premium Twilio Verify */}
                    {!lead.verification?.phone_verified && (
                      <button
                        onClick={() => premiumVerify(i)}
                        disabled={verifying[i]}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 text-[10px] font-semibold text-amber-800 hover:border-amber-400 hover:shadow-sm transition-all disabled:opacity-50"
                        data-testid={`premium-verify-btn-${i}`}
                      >
                        <i className={`fa-solid ${verifying[i] ? 'fa-spinner fa-spin' : 'fa-phone-volume'} text-amber-600`} />
                        {verifying[i] ? 'Verifying...' : 'Premium Verify'}
                        <span className="text-[8px] text-amber-600 ml-1 font-bold">$1.35</span>
                      </button>
                    )}

                    {/* Show "Verified" badge if already premium verified */}
                    {lead.verification?.phone_verified && lead.verification?.phone_source === 'twilio_lookup_v2' && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-50 border border-green-200 text-[10px] font-bold text-green-700">
                        <i className="fa-solid fa-badge-check text-green-500" />Premium Verified
                      </span>
                    )}

                    {/* Nationwide Permit History (Shovels) */}
                    <button
                      onClick={() => pullPermitHistory(i)}
                      disabled={permitLoading[i]}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-teal-50 to-cyan-50 border border-teal-200 text-[10px] font-semibold text-teal-800 hover:border-teal-400 hover:shadow-sm transition-all disabled:opacity-50"
                      data-testid={`permit-history-btn-${i}`}
                    >
                      <i className={`fa-solid ${permitLoading[i] ? 'fa-spinner fa-spin' : 'fa-file-contract'} text-teal-600`} />
                      {permitLoading[i] ? 'Searching...' : 'Permit History'}
                      <span className="text-[8px] text-teal-600 ml-1 font-bold">NATIONWIDE</span>
                    </button>
                  </div>

                  {/* Permit History Result */}
                  {permitHist[i] && permitHist[i].found && (
                    <div className="mb-3 p-3 rounded-lg bg-teal-50/50 border border-teal-100" data-testid={`permit-history-result-${i}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[11px] font-bold text-teal-800"><i className="fa-solid fa-file-contract mr-1" />{permitHist[i].permit_count} nationwide permit(s)</span>
                        {permitHist[i].has_existing_solar ? (
                          <span className="text-[9px] font-bold text-red-700 bg-red-100 px-2 py-0.5 rounded-full"><i className="fa-solid fa-triangle-exclamation mr-1" />EXISTING SOLAR</span>
                        ) : permitHist[i].signal ? (
                          <span className="text-[9px] font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">{permitHist[i].signal}</span>
                        ) : null}
                      </div>
                      {permitHist[i].tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {permitHist[i].tags.slice(0, 8).map(t => (
                            <span key={t} className="px-2 py-0.5 rounded-full bg-white border border-teal-100 text-[9px] font-bold text-teal-700 uppercase">{t.replace(/_/g, ' ')}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {permitHist[i] && !permitHist[i].found && (
                    <p className="mb-3 text-[10px] text-text-muted" data-testid={`permit-history-empty-${i}`}><i className="fa-solid fa-circle-info mr-1" />No nationwide permit records for this address.</p>
                  )}

                  {/* Notes */}
                  <p className="text-xs text-text-secondary leading-relaxed mb-3">{lead.notes}</p>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                    {[
                      { label: 'System', value: `${lead.recommended_system_kw || 0} kW`, real: true },
                      { label: 'Savings/yr', value: `$${(lead.estimated_savings || 0).toLocaleString()}`, real: true },
                      { label: 'Annual kWh', value: `${(lead.annual_kwh || 0).toLocaleString()}`, real: true },
                      { label: 'Elec Rate', value: lead.utility_rate ? `$${lead.utility_rate}/kWh` : '—', real: !!lead.utility_rate },
                      { label: 'Area Income', value: lead.area_median_income ? `$${(lead.area_median_income / 1000).toFixed(0)}k` : '—', real: !!lead.area_median_income },
                      { label: 'Permit $', value: lead.permit_valuation ? `$${(lead.permit_valuation || 0).toLocaleString()}` : (lead.property_value ? `$${lead.property_value.toLocaleString()}` : '—'), real: !!(lead.permit_valuation || lead.property_value) },
                    ].map(s => (
                      <div key={s.label} className="text-center p-2 rounded-lg bg-surface-alt relative">
                        <p className="text-[10px] text-text-muted uppercase tracking-wider">{s.label}</p>
                        <p className="text-xs font-bold text-text-primary mt-0.5 tabular-nums">{s.value}</p>
                        {s.real && <div className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-green-500" title="Real / calculated data" />}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {!scanning && scanDone && leads.length === 0 && (
        <div className="card-interactive py-20 text-center">
          <i className="fa-solid fa-file-circle-xmark text-4xl text-text-muted opacity-20 mb-3" />
          <p className="font-medium text-text-primary">No permits found for this location</p>
          <p className="text-xs text-text-muted mt-1">Try a different zip code or city name</p>
        </div>
      )}

      {/* Initial State */}
      {!scanning && !scanDone && (
        <div className="card-interactive py-16 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary/10 flex items-center justify-center">
            <i className="fa-solid fa-tower-broadcast text-primary text-2xl" />
          </div>
          <h3 className="font-heading text-lg font-semibold text-text-primary mb-2">Ready to Scan</h3>
          <p className="text-sm text-text-muted max-w-md mx-auto">Enter a covered city (or ZIP) to pull <strong>real building permits</strong> — roofing, electrical, pool, HVAC & re-roofs — as hot solar-lead triggers, enriched with in-house solar estimates and area income.</p>
          <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-50 border border-green-200 text-xs text-green-700 font-medium">
            <i className="fa-solid fa-circle-check text-green-500" />
            Live data across NYC (5 boroughs), New Jersey & 20+ open-data portals — 100% free, no API keys required
          </div>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-lg mx-auto text-left">
            {[
              { icon: 'fa-file-contract', label: 'Permit Data', desc: 'Type, value, date' },
              { icon: 'fa-house', label: 'Property Intel', desc: 'Value, age, sqft' },
              { icon: 'fa-phone-volume', label: 'Verified Contacts', desc: 'Phone + Name confirmed' },
              { icon: 'fa-ranking-star', label: 'Solar Score', desc: 'AI-powered 0-100' },
            ].map(f => (
              <div key={f.label} className="p-3 rounded-xl bg-surface-alt border border-border/50">
                <i className={`fa-solid ${f.icon} text-primary text-sm mb-2 block`} />
                <p className="text-xs font-semibold text-text-primary">{f.label}</p>
                <p className="text-[10px] text-text-muted">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
